import React, { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import BillingPeriodCell from '../BillingPeriodCell';
import { ConfirmationDialog } from '@roger/ui-components';
import {
  getActiveBillingCycles,
  undoBillingCycleMarkedPaid,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { PermissionSlug } from '../../../constants/featureFlags';
import { useStore } from '../../../store';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { daysToPayDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import {
  hasInvoiceRow,
  manualMoveRefusal,
  showManualMoveFailures,
} from '../manualMoves';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn, writtenOffColumn } from '../billingTableUtils';
import { formatMoneyDisplay } from '../../../utils/currencyUtils';
import { PatientCell } from '../PatientCell';
import { InactiveEpisodeBadge } from '../InactiveEpisodeBadge';
import { InsuranceCell } from '../InsuranceCell';
import { NoRemittancesNotice } from '../NoRemittances';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import { WriteOffAction } from '../WriteOffAction';
import { RemittanceLinkAction } from '../RemittanceLinkAction';

interface EOEPaidTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPaidTab: React.FC<EOEPaidTabProps> = ({ onCountsInvalidated }) => {
  const [undoPaidConfirmOpen, setUndoPaidConfirmOpen] = useState(false);
  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );
  const query = useBillingTableQuery('paid', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    isSubmitting,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    showError,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'paid',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PAID'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Paid" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
    getBillType: item => item.bill_type,
  });

  const handleConfirmUndoPaid = () => {
    setUndoPaidConfirmOpen(false);
    void mutateSelected(
      id => undoBillingCycleMarkedPaid({ path: { billing_cycle_id: id } }),
      'Some billing cycles could not be moved back to Pending Payment',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures =>
        showManualMoveFailures(
          failures,
          showError,
          'Some billing cycles could not be moved back to Pending Payment'
        )
    );
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <PatientCell
              name={row.original.patient_name}
              mrn={row.original.patient_mrn}
              patientId={row.original.patient_id}
            />
            {row.original.episode_status === 'cancelled' && (
              <InactiveEpisodeBadge className='self-start' />
            )}
          </div>
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
            receivesRemittances={row.original.receives_remittances}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
        cell: ({ row }) => (
          <span className='text-sm'>{row.original.claim_number}</span>
        ),
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
        sortingFn: currencySortingFn,
      },
      {
        id: 'total_payment',
        accessorKey: 'total_payment',
        header: 'Total Payment',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
      },
      {
        id: 'expected',
        accessorKey: 'total_anticipated_payment',
        header: 'Expected',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
      },
      {
        id: 'variance',
        accessorKey: 'variance',
        header: 'Variance (paid - expected)',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
      },
      writtenOffColumn,
      {
        id: 'days_to_pay',
        header: 'Days to Pay',
        accessorFn: row => daysToPayDays(row.billed_at, row.paid_date) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            mode='daysToPay'
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
            paidDate={row.original.paid_date}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            billType={row.original.bill_type}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <>
                <RemittanceLinkAction billingCycle={row.original} />
                <ClaimWorksheetViewerAction billingCycle={row.original} />
                <WriteOffAction
                  billingCycleId={row.original.billing_cycle_id}
                  claim={row.original}
                />
              </>
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <div className='flex flex-col gap-4'>
      <NoRemittancesNotice rows={data} />
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={
          canSubmitClaims
            ? {
                selectedIds,
                onToggleId: handleToggleId,
                onToggleAllVisible: handleToggleAll,
              }
            : undefined
        }
      />
      <ConfirmationDialog
        open={undoPaidConfirmOpen}
        title='Undo Mark as Paid?'
        message='Moves the selected invoice claims back to Pending Payment. A claim that a completed remittance paid cannot be moved; reopen that remittance instead.'
        confirmLabel='Undo Mark as Paid'
        onAccept={handleConfirmUndoPaid}
        onCancel={() => setUndoPaidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={
          canSubmitClaims
            ? [
                {
                  key: 'undo-paid',
                  label: 'Undo Mark as Paid',
                  isVisible: hasInvoiceRow,
                  disabledReason: () =>
                    isSubmitting || isLoading
                      ? 'Action in progress'
                      : manualMoveRefusal('Undo Mark as Paid', selectedItems),
                  run: () => setUndoPaidConfirmOpen(true),
                },
              ]
            : []
        }
      />
    </div>
  );
};

export default EOEPaidTab;
